import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Airplane, Users, Clock, CurrencyDollar, ListChecks,
  Trash, Plus, Printer, SignOut, Buildings, PencilSimple, FloppyDisk, X, MagnifyingGlass, Package, Radio, ClockCounterClockwise,
} from "@phosphor-icons/react";
import LiveTab from "./LiveTab";

const TABS = [
  { id: "punches", label: "Punches", icon: ListChecks },
  { id: "summary", label: "Summary", icon: CurrencyDollar },
  { id: "live", label: "Live", icon: Radio },
  { id: "workers", label: "Workers", icon: Users },
  { id: "shifts", label: "Shifts", icon: Clock },
  { id: "payroll", label: "Payroll", icon: CurrencyDollar },
  { id: "company", label: "Company", icon: Buildings },
];

export default function PunchAdmin() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("punches");

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "punch_admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-zinc-950 text-white px-6 py-4 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-2">
          <Airplane size={22} weight="duotone" className="text-[#FF4F00]" />
          <div>
            <div className="font-display font-black text-base leading-none">TOM <span className="text-[#FF4F00]">CARGO</span> DOCS</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Punch Card · Admin</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && (
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="switch-to-docs-btn"
              className="text-xs border border-[#FF4F00] text-[#FF4F00] px-3 py-1 hover:bg-[#FF4F00] hover:text-white transition-colors flex items-center gap-1"
              title="Open the Cargo Documents dashboard"
            >
              <Package size={12} /> Switch → Cargo Docs
            </button>
          )}
          <button onClick={doLogout} data-testid="admin-logout-btn" className="text-xs text-zinc-400 flex items-center gap-1 border border-zinc-700 px-2 py-1 hover:bg-zinc-800">
            <SignOut size={12} /> {user.name}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="flex gap-1 border-b border-zinc-200 mb-6 flex-wrap print:hidden">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors duration-150 ${tab === id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              <Icon size={14} /> {label}
              {id === "live" && <LiveTabBadge onClick={() => setTab("live")} />}
            </button>
          ))}
        </div>

        {tab === "punches" && <PunchesTab />}
        {tab === "summary" && <SummaryTab />}
        {tab === "live" && <LiveTab />}
        {tab === "workers" && <WorkersTab />}
        {tab === "shifts" && <ShiftsTab />}
        {tab === "payroll" && <PayrollTab />}
        {tab === "company" && <CompanyTab />}
      </div>
    </div>
  );
}

// Small red-dot badge on the "Live" tab button — polls active workers every 10s so
// admin can see at a glance whether anyone is currently punched in.
function LiveTabBadge() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/punch/live/active");
        if (!cancelled) setCount((data.active || []).length);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!count) return null;
  return (
    <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono bg-red-600 text-white px-1.5 py-0.5 animate-pulse" data-testid="live-badge">
      <span className="w-1.5 h-1.5 rounded-full bg-white" />
      {count}
    </span>
  );
}


/* ---------------- Punches ---------------- */
// Convert an ISO timestamp to a value string for <input type="datetime-local"> in the browser's LOCAL zone.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

function PunchesTab() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ worker_id: "", date_from: "", date_to: "" });
  const [workers, setWorkers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/punch/punches", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { api.get("/punch/workers").then(({ data }) => setWorkers(data.filter(w => !w.is_resigned))); load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this punch permanently?")) return;
    try { await api.delete(`/punch/punches/${id}`); toast.success("Punch deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Jump to a punch row and enter edit mode.
  const jumpToPunch = (punchId) => {
    setEditingId(punchId);
    setHighlightId(punchId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="punch-edit-${punchId}"]`) || document.querySelector(`[data-testid="punch-${punchId}"]`);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 120);
  };

  return (
    <div>
      <DailySummaryPanel workers={workers} filters={filters} setFilters={setFilters} onJump={jumpToPunch} />

      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <select value={filters.worker_id} onChange={(e) => setFilters({ ...filters, worker_id: e.target.value })}
          className="border border-zinc-200 px-3 py-2 text-sm" data-testid="filter-worker">
          <option value="">All workers</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="filter-from" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="filter-to" />
        <button onClick={load} className="bg-[#002FA7] text-white text-sm px-4 py-2" data-testid="load-punches-btn">Load raw punches</button>
        <div className="ml-auto">
          <button onClick={() => setShowAdd(true)} className="bg-[#FF4F00] text-white text-sm px-4 py-2 flex items-center gap-1" data-testid="add-missing-punch-btn">
            <Plus size={14} /> Add missing punch
          </button>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Time</th>
              <th className="text-left px-4 py-2">Worker</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Shift</th>
              <th className="text-left px-4 py-2">GPS</th>
              <th className="text-left px-4 py-2">Photo</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">No punches</td></tr> : items.map((p) => (
              editingId === p.id ? (
                <PunchEditRow key={p.id} p={p} onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />
              ) : (
                <tr key={p.id} className={`border-b border-zinc-100 transition-colors ${highlightId === p.id ? "bg-amber-50" : ""}`} data-testid={`punch-${p.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">
                    {new Date(p.timestamp).toLocaleString()}
                    {p.edited_at && <span className="ml-1 text-[9px] text-amber-700 font-mono uppercase" title={`Edited ${new Date(p.edited_at).toLocaleString()} by ${p.edited_by || ""}`}>·edit</span>}
                    {p.manual && <span className="ml-1 text-[9px] text-[#FF4F00] font-mono uppercase" title={`Manual entry by ${p.created_by || ""}`}>·manual</span>}
                  </td>
                  <td className="px-4 py-2">{p.worker_name}</td>
                  <td className="px-4 py-2"><span className={`text-[10px] font-mono border px-1.5 py-0.5 ${p.punch_type === "in" ? "border-emerald-300 text-emerald-800 bg-emerald-50" : "border-red-300 text-red-800 bg-red-50"}`}>{p.punch_type.toUpperCase()}</span></td>
                  <td className="px-4 py-2 font-mono text-xs">{p.shift_code || "—"}{p.shift_is_overtime ? " (OT)" : ""}</td>
                  <td className="px-4 py-2 font-mono text-[10px]">{p.gps_lat ? `${p.gps_lat.toFixed(4)}, ${p.gps_lng.toFixed(4)}` : "—"}</td>
                  <td className="px-4 py-2">{p.photo_path ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/punch/photo/${p.id}?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline">view</a> : <span className="text-xs text-zinc-400">—</span>}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditingId(p.id)} className="text-xs bg-zinc-100 hover:bg-zinc-200 px-2 py-1 mr-1 flex items-center gap-1 inline-flex" data-testid={`edit-punch-${p.id}`}><PencilSimple size={12} /> Edit</button>
                    <button onClick={() => del(p.id)} className="text-xs text-red-700 hover:bg-red-50 p-1" data-testid={`delete-punch-${p.id}`}><Trash size={12} /></button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddPunchModal workers={workers} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function DailySummaryPanel({ workers, filters, setFilters, onJump }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [wid, setWid] = useState("");
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (workers[0] && !wid) setWid(workers[0].id); }, [workers, wid]);

  const load = async () => {
    if (!wid) { toast.error("Select a worker"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/punch/daily-summary", { params: { worker_id: wid, date_from: from, date_to: to } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const badge = (status) => {
    const map = {
      on_time:   "bg-emerald-50 border-emerald-300 text-emerald-800",
      late:      "bg-amber-50 border-amber-300 text-amber-800",
      half_day:  "bg-orange-50 border-orange-300 text-orange-800",
      full_leave:"bg-red-50 border-red-300 text-red-800",
      off_day:   "bg-zinc-50 border-zinc-300 text-zinc-600",
    };
    return `text-[10px] font-mono border px-1.5 py-0.5 ${map[status] || map.off_day}`;
  };

  return (
    <div className="bg-white border border-zinc-200 mb-4" data-testid="daily-summary-panel">
      <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap gap-2 items-end">
        <div className="font-display font-black uppercase text-sm mr-2">Daily Summary</div>
        <select value={wid} onChange={(e) => setWid(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm" data-testid="summary-worker">
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm font-mono" data-testid="summary-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm font-mono" data-testid="summary-to" />
        <button onClick={load} className="bg-[#002FA7] text-white text-xs px-3 py-1.5" data-testid="summary-load-btn">{loading ? "…" : "Show"}</button>
        <span className="ml-auto text-[10px] text-zinc-500">Click ✏ on IN or OUT to jump straight to that punch's edit form below.</span>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase font-mono text-zinc-500">
              <tr>
                <th className="text-left px-3 py-1.5">Date</th>
                <th className="text-left px-3 py-1.5">IN</th>
                <th className="text-left px-3 py-1.5">OUT</th>
                <th className="text-left px-3 py-1.5">Hrs</th>
                <th className="text-left px-3 py-1.5">Status</th>
                <th className="text-left px-3 py-1.5">OT</th>
                <th className="text-right px-3 py-1.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-100" data-testid={`summary-row-${r.date}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                  <td className="px-3 py-1.5 font-mono">
                    {r.in_local}
                    <button onClick={() => onJump(r.in_punch_id)} className="ml-1 text-[10px] text-[#002FA7] hover:underline" title="Edit this IN punch" data-testid={`jump-in-${r.date}`}>✏</button>
                  </td>
                  <td className="px-3 py-1.5 font-mono">
                    {r.out_local}
                    <button onClick={() => onJump(r.out_punch_id)} className="ml-1 text-[10px] text-[#002FA7] hover:underline" title="Edit this OUT punch" data-testid={`jump-out-${r.date}`}>✏</button>
                  </td>
                  <td className="px-3 py-1.5 font-mono">{r.hours}h</td>
                  <td className="px-3 py-1.5">
                    <span className={badge(r.status)}>{r.status.replace("_"," ").toUpperCase()}</span>
                    <span className="ml-2 text-[11px] text-zinc-600">{r.label}</span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {r.ot_shifts.length > 0
                      ? <span><b className="text-[#FF4F00]">{r.ot_count} OT</b> · {r.ot_shifts.map((s) => s.code).join(" + ")}</span>
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {r.ot_total > 0 && <span className="text-emerald-700">+RM{r.ot_total.toFixed(2)}</span>}
                    {r.penalty > 0 && <span className="text-red-700 ml-2">-RM{r.penalty.toFixed(2)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length === 0 && !loading && (
        <div className="px-4 py-6 text-center text-xs text-zinc-400">Select worker + date range and click Show.</div>
      )}
    </div>
  );
}

function SummaryTab() {
  const [workers, setWorkers] = useState([]);
  const [wid, setWid] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/punch/workers").then(({ data }) => { const act = data.filter(w => !w.is_resigned); setWorkers(act); if (act[0]) setWid(act[0].id); }).catch(() => {}); }, []);

  const load = async () => {
    if (!wid) { toast.error("Select a worker"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/punch/daily-summary", { params: { worker_id: wid, date_from: from, date_to: to } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const totals = rows.reduce((acc, r) => {
    acc.ot_entries += r.ot_count || 0;
    acc.ot_total += r.ot_total || 0;
    acc.late_days += r.status === "late" ? 1 : 0;
    acc.half_days += r.status === "half_day" ? 1 : 0;
    acc.full_leaves += r.status === "full_leave" ? 1 : 0;
    acc.late_total += r.penalty || 0;
    acc.on_time += r.status === "on_time" ? 1 : 0;
    acc.off_days += r.status === "off_day" ? 1 : 0;
    return acc;
  }, { ot_entries: 0, ot_total: 0, late_days: 0, half_days: 0, full_leaves: 0, late_total: 0, on_time: 0, off_days: 0 });

  const rm = (n) => `RM ${Number(n || 0).toFixed(2)}`;
  const worker = workers.find((w) => w.id === wid);
  const monthLabel = (() => {
    const d = new Date(to || from || Date.now());
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }).toUpperCase();
  })();

  const badge = (status) => {
    const map = {
      on_time:   "bg-emerald-50 border-emerald-300 text-emerald-800",
      late:      "bg-amber-50 border-amber-300 text-amber-800",
      half_day:  "bg-orange-50 border-orange-300 text-orange-800",
      full_leave:"bg-red-50 border-red-300 text-red-800",
      off_day:   "bg-zinc-50 border-zinc-300 text-zinc-600",
    };
    return `text-[10px] font-mono border px-1.5 py-0.5 ${map[status] || map.off_day}`;
  };

  return (
    <div data-testid="summary-tab">
      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap gap-2 items-end">
        <div>
          <div className="text-[10px] uppercase font-mono text-zinc-500 mb-0.5">Worker</div>
          <select value={wid} onChange={(e) => setWid(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm" data-testid="summary-tab-worker">
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase font-mono text-zinc-500 mb-0.5">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm font-mono" data-testid="summary-tab-from" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-mono text-zinc-500 mb-0.5">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-zinc-200 px-2 py-1 text-sm font-mono" data-testid="summary-tab-to" />
        </div>
        <button onClick={load} className="bg-[#002FA7] text-white text-xs px-4 py-2" data-testid="summary-tab-load">{loading ? "…" : "Show"}</button>
        <div className="ml-auto text-[11px] text-zinc-500 max-w-sm">
          Reference-only totals from actual punches. Check & approve, then key the same figures into D/E/F/G/H on the OT slip.
        </div>
      </div>

      {rows.length > 0 && (
        <div className="bg-white border-2 border-[#002FA7] mb-4" data-testid="summary-tab-totals">
          <div className="bg-[#002FA7] text-white px-4 py-2 flex items-baseline justify-between">
            <div className="font-display font-black text-sm uppercase tracking-wider">Month Totals · {worker?.name || "—"} · {monthLabel}</div>
            <div className="text-[10px] font-mono opacity-80">{from} → {to}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-y divide-zinc-200">
            <TotalCard label="OT entries" value={totals.ot_entries} sub={rm(totals.ot_total)} accent="text-emerald-700" testId="tot-ot" />
            <TotalCard label="Late days" value={totals.late_days} sub={`- ${rm(totals.late_total)}`} accent="text-red-700" testId="tot-late" />
            <TotalCard label="Half-days" value={totals.half_days} sub="worked partial hours" accent="text-orange-700" testId="tot-half" />
            <TotalCard label="Full leaves" value={totals.full_leaves} sub="did not clock in" accent="text-red-700" testId="tot-full" />
            <TotalCard label="Off days (Sun)" value={totals.off_days} sub="P. Holidays" accent="text-zinc-700" testId="tot-off" />
            <TotalCard label="On time" value={totals.on_time} sub="normal working days" accent="text-emerald-700" testId="tot-ontime" />
            <TotalCard label="Total days" value={rows.length} sub="days in range" accent="text-zinc-900" testId="tot-days" />
            <TotalCard label="Net (OT − Late)" value={rm(totals.ot_total - totals.late_total)} sub="reference only" accent="text-[#002FA7]" testId="tot-net" bigValue />
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-zinc-200" data-testid="summary-tab-table">
          <div className="bg-zinc-900 text-white text-xs uppercase font-mono px-3 py-1.5 tracking-wider">Daily Breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase font-mono text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-left px-3 py-1.5">IN</th>
                  <th className="text-left px-3 py-1.5">OUT</th>
                  <th className="text-left px-3 py-1.5">Hrs</th>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="text-left px-3 py-1.5">OT</th>
                  <th className="text-right px-3 py-1.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                    <td className="px-3 py-1.5 font-mono">{r.in_local}</td>
                    <td className="px-3 py-1.5 font-mono">{r.out_local}</td>
                    <td className="px-3 py-1.5 font-mono">{r.hours}h</td>
                    <td className="px-3 py-1.5">
                      <span className={badge(r.status)}>{r.status.replace("_"," ").toUpperCase()}</span>
                      <span className="ml-2 text-[11px] text-zinc-600">{r.label}</span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {r.ot_shifts.length > 0
                        ? <span><b className="text-[#FF4F00]">{r.ot_count} OT</b> · {r.ot_shifts.map((s) => s.code).join(" + ")}</span>
                        : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {r.ot_total > 0 && <span className="text-emerald-700">+RM{r.ot_total.toFixed(2)}</span>}
                      {r.penalty > 0 && <span className="text-red-700 ml-2">-RM{r.penalty.toFixed(2)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length === 0 && !loading && (
        <div className="bg-white border border-dashed border-zinc-300 px-6 py-10 text-center text-sm text-zinc-500">
          Select a worker + date range and click <b>Show</b> to view the month's actual figures.
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, sub, accent = "text-zinc-900", testId, bigValue }) {
  return (
    <div className="p-3">
      <div className="text-[10px] uppercase font-mono text-zinc-500">{label}</div>
      <div className={`font-display font-black ${bigValue ? "text-xl" : "text-3xl"} ${accent}`} data-testid={testId}>{value}</div>
      <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function PunchEditRow({ p, onCancel, onSaved }) {
  const [ts, setTs] = useState(isoToLocalInput(p.timestamp));
  const [type, setType] = useState(p.punch_type);
  const [notes, setNotes] = useState(p.notes || "");
  const save = async () => {
    const iso = localInputToIso(ts);
    if (!iso) { toast.error("Invalid time"); return; }
    try {
      await api.patch(`/punch/punches/${p.id}`, { timestamp: iso, punch_type: type, notes });
      toast.success("Punch updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <tr className="border-b border-[#002FA7]/30 bg-[#002FA7]/5" data-testid={`punch-edit-${p.id}`}>
      <td className="px-4 py-2">
        <input type="datetime-local" value={ts} onChange={(e) => setTs(e.target.value)} className="border border-zinc-200 px-2 py-1 font-mono text-xs" data-testid={`punch-time-input-${p.id}`} />
      </td>
      <td className="px-4 py-2 text-xs">{p.worker_name}</td>
      <td className="px-4 py-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border border-zinc-200 px-1 py-1 text-xs font-mono uppercase" data-testid={`punch-type-input-${p.id}`}>
          <option value="in">IN</option>
          <option value="out">OUT</option>
        </select>
      </td>
      <td className="px-4 py-2 text-[10px] text-zinc-500 font-mono" colSpan={3}>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (e.g. forgot to clock in)" className="w-full border border-zinc-200 px-2 py-1 text-xs" data-testid={`punch-note-input-${p.id}`} />
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <button onClick={save} className="text-xs bg-emerald-600 text-white px-2 py-1 mr-1 inline-flex items-center gap-1" data-testid={`save-punch-${p.id}`}><FloppyDisk size={12} /> Save</button>
        <button onClick={onCancel} className="text-xs border border-zinc-200 px-2 py-1 inline-flex items-center gap-1"><X size={12} /></button>
      </td>
    </tr>
  );
}

function AddPunchModal({ workers, onClose, onSaved }) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const defaultTs = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const [f, setF] = useState({ worker_id: workers[0]?.id || "", punch_type: "in", timestamp: defaultTs, notes: "" });

  const save = async () => {
    if (!f.worker_id) { toast.error("Select worker"); return; }
    const iso = localInputToIso(f.timestamp);
    if (!iso) { toast.error("Invalid time"); return; }
    try {
      await api.post("/punch/punches/manual", { worker_id: f.worker_id, punch_type: f.punch_type, timestamp: iso, notes: f.notes });
      toast.success("Missing punch added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-zinc-900 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="add-punch-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-black text-lg">Add missing punch</div>
          <button onClick={onClose} className="text-zinc-500"><X size={16} /></button>
        </div>
        <div className="text-xs text-zinc-500 mb-3">Use this when a worker forgot to clock in/out. No photo required — the punch will be tagged <b>MANUAL</b>.</div>
        <div className="space-y-2">
          <select value={f.worker_id} onChange={(e) => setF({ ...f, worker_id: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm" data-testid="add-punch-worker">
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <div className="flex gap-2">
            <select value={f.punch_type} onChange={(e) => setF({ ...f, punch_type: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm font-mono uppercase" data-testid="add-punch-type">
              <option value="in">IN</option>
              <option value="out">OUT</option>
            </select>
            <input type="datetime-local" value={f.timestamp} onChange={(e) => setF({ ...f, timestamp: e.target.value })} className="flex-1 border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="add-punch-time" />
          </div>
          <input placeholder="Note (e.g. forgot to clock in)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm" data-testid="add-punch-notes" />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} className="bg-[#002FA7] text-white text-sm px-4 py-2 flex items-center gap-1" data-testid="add-punch-save"><FloppyDisk size={14} /> Add punch</button>
          <button onClick={onClose} className="text-sm border border-zinc-200 px-4 py-2">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Workers ---------------- */
const BLANK_ALLOWANCE = { label: "", amount: 0 };
const STATUTORY_SLOTS = [
  { key: "epf",    defaultLabel: "EPF",   defaultRate: 0.11  },
  { key: "socso",  defaultLabel: "SOCSO", defaultRate: 0.005 },
  { key: "sip",    defaultLabel: "SIP",   defaultRate: 0.002 },
  { key: "extra1", defaultLabel: "",      defaultRate: 0     },
  { key: "extra2", defaultLabel: "",      defaultRate: 0     },
];
const PAY_TYPES = [
  { key: "salary",   label: "Monthly salary",  hint: "Fixed RM basic per month" },
  { key: "hourly",   label: "Hourly (part-time)", hint: "RM/hour × hours worked from punch-in/out" },
  { key: "per_trip", label: "Per trip (driver)", hint: "RM/trip × each punch-in + punch-out pair" },
];
const BLANK_WORKER = {
  name: "", email: "", password: "", phone: "", ic_no: "",
  pay_type: "salary",
  basic_salary: 0,
  hourly_rate: 0,
  per_trip_rate: 0,
  epf_label: "EPF",     epf_amount: "",     epf_employer_amount: "",
  socso_label: "SOCSO", socso_amount: "",   socso_employer_amount: "",
  sip_label: "SIP",     sip_amount: "",     sip_employer_amount: "",
  extra1_label: "",     extra1_amount: "",  extra1_employer_amount: "",
  extra2_label: "",     extra2_amount: "",  extra2_employer_amount: "",
  allowances: [],
};

function WorkersTab() {
  const [ws, setWs] = useState([]);
  const [form, setForm] = useState(BLANK_WORKER);
  const [editing, setEditing] = useState(null); // worker being edited
  const [showResigned, setShowResigned] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [auditKey, setAuditKey] = useState(0);
  const load = async () => { try { const { data } = await api.get("/punch/workers"); setWs(data); } catch (e) { toast.error(formatApiError(e)); } };
  const toggleResign = async (w) => {
    const action = w.is_resigned ? "Reinstate" : "Resign";
    const reason = window.prompt(`${action} ${w.name}? Optional reason:`, "");
    if (reason === null) return;
    try {
      await api.patch(`/punch/workers/${w.id}/resign`, { resigned: !w.is_resigned, reason });
      toast.success(`${w.name} ${w.is_resigned ? "reinstated" : "marked resigned"}`);
      setAuditKey((k) => k + 1);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        pay_type: form.pay_type || "salary",
        basic_salary: Number(form.basic_salary) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        per_trip_rate: Number(form.per_trip_rate) || 0,
        epf_label: (form.epf_label || "").trim(),
        epf_amount: form.epf_amount === "" ? null : Number(form.epf_amount),
        epf_employer_amount: form.epf_employer_amount === "" ? null : Number(form.epf_employer_amount),
        socso_label: (form.socso_label || "").trim(),
        socso_amount: form.socso_amount === "" ? null : Number(form.socso_amount),
        socso_employer_amount: form.socso_employer_amount === "" ? null : Number(form.socso_employer_amount),
        sip_label: (form.sip_label || "").trim(),
        sip_amount: form.sip_amount === "" ? null : Number(form.sip_amount),
        sip_employer_amount: form.sip_employer_amount === "" ? null : Number(form.sip_employer_amount),
        extra1_label: (form.extra1_label || "").trim(),
        extra1_amount: form.extra1_amount === "" ? null : Number(form.extra1_amount),
        extra1_employer_amount: form.extra1_employer_amount === "" ? null : Number(form.extra1_employer_amount),
        extra2_label: (form.extra2_label || "").trim(),
        extra2_amount: form.extra2_amount === "" ? null : Number(form.extra2_amount),
        extra2_employer_amount: form.extra2_employer_amount === "" ? null : Number(form.extra2_employer_amount),
        allowances: (form.allowances || []).filter((a) => a.label).map((a) => ({ label: a.label, amount: Number(a.amount) || 0 })),
      };
      await api.post("/punch/workers", payload);
      toast.success("Worker added");
      setForm(BLANK_WORKER);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => { if (!window.confirm("Remove worker?")) return; try { await api.delete(`/punch/workers/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white border border-zinc-200 p-4" data-testid="add-worker-form">
        <div className="font-display font-black text-lg mb-3">Add worker</div>
        <div className="grid md:grid-cols-3 gap-2">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="worker-name-input" />
          <input required type="email" placeholder="Email (login)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="worker-email-input" />
          <input required type="password" placeholder="Password (min 6)" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="worker-pw-input" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="worker-phone-input" />
          <input placeholder="IC No" value={form.ic_no} onChange={(e) => setForm({ ...form, ic_no: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="worker-ic-input" />
        </div>
        <PayTypeEditor value={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix="new" />
        <StatutoryEditor value={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix="new" />
        <AllowancesEditor value={form.allowances} onChange={(a) => setForm({ ...form, allowances: a })} testPrefix="new" />
        <button type="submit" className="mt-3 bg-[#002FA7] text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="add-worker-btn"><Plus size={14} /> Add worker</button>
      </form>

      <div className="bg-white border border-zinc-200 p-4">
        <div className="font-display font-black text-lg mb-3 flex items-center justify-between">
          <span>Workers ({ws.filter(w => showResigned || !w.is_resigned).length}{showResigned ? ` of ${ws.length}` : ""})</span>
          <label className="text-xs font-mono text-zinc-500 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showResigned} onChange={(e) => setShowResigned(e.target.checked)} data-testid="show-resigned-toggle" className="accent-[#002FA7]"/>
            Show resigned
          </label>
        </div>
        {ws.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No workers yet.</div>}
        {ws.filter(w => showResigned || !w.is_resigned).map((w) => (
          <div key={w.id} className={`border-b border-zinc-100 last:border-0 py-3 ${w.is_resigned ? "opacity-60" : ""}`} data-testid={`worker-${w.id}`}>
            {editing === w.id ? (
              <WorkerEditor
                worker={w}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
              />
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {w.name}
                    <span className="text-xs text-zinc-500 font-mono">· {w.email}</span>
                    {w.is_resigned && <span className="text-[10px] font-mono border border-zinc-300 bg-zinc-100 text-zinc-600 px-1.5 py-0.5 uppercase">Resigned</span>}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">
                    IC: {w.ic_no || "—"} · Pay: <span className="text-[#002FA7] uppercase">{w.pay_type || "salary"}</span>
                    {(!w.pay_type || w.pay_type === "salary") && ` · Basic: RM${Number(w.basic_salary || 0).toFixed(2)}`}
                    {w.pay_type === "hourly" && ` · RM${Number(w.hourly_rate || 0).toFixed(2)}/hr`}
                    {w.pay_type === "per_trip" && ` · RM${Number(w.per_trip_rate || 0).toFixed(2)}/trip`}
                    {" · "}{STATUTORY_SLOTS.filter((s) => (w[`${s.key}_label`] || "").trim()).map((s) => `${w[`${s.key}_label`]} RM${Number(w[`${s.key}_amount`] || 0).toFixed(2)}`).join(" · ")} · Allowances: RM{((w.allowances || []).reduce((s, a) => s + (Number(a.amount) || 0), 0)).toFixed(2)}
                  </div>
                  {(w.allowances || []).length > 0 && (
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {(w.allowances || []).map((a, i) => (
                        <span key={i} className="mr-3">{a.label}: RM{Number(a.amount || 0).toFixed(2)}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(w.id)} className="text-xs bg-zinc-100 hover:bg-zinc-200 px-2 py-1 flex items-center gap-1" data-testid={`edit-worker-${w.id}`}><PencilSimple size={12} /> Edit</button>
                  <button onClick={() => toggleResign(w)} className={`text-xs px-2 py-1 flex items-center gap-1 ${w.is_resigned ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "bg-amber-50 text-amber-800 hover:bg-amber-100"}`} data-testid={`resign-worker-${w.id}`}>{w.is_resigned ? "Reinstate" : "Resign"}</button>
                  <button onClick={() => setHistoryFor(historyFor === w.id ? null : w.id)} className="text-xs bg-zinc-100 hover:bg-zinc-200 px-2 py-1 flex items-center gap-1" data-testid={`history-worker-${w.id}`}><ClockCounterClockwise size={12} /> History</button>
                  <button onClick={() => del(w.id)} className="text-xs text-red-700 hover:bg-red-50 p-1"><Trash size={12} /></button>
                </div>
              </div>
            )}
            {historyFor === w.id && <AuditTable url={`/punch/workers/${w.id}/audit`} refreshKey={auditKey} testid={`history-panel-${w.id}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 p-4" data-testid="resign-audit-log">
        <div className="font-display font-black text-lg mb-3">Resign / Reinstate audit log</div>
        <AuditTable url="/punch/audit-log" refreshKey={auditKey} showWorker testid="audit-log-table" />
      </div>
    </div>
  );
}

function AuditTable({ url, refreshKey, showWorker, testid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(url).then(({ data }) => setRows(data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); }); }, [url, refreshKey]);
  if (rows === null) return <div className="text-xs text-zinc-500 py-2 font-mono">Loading…</div>;
  if (rows.length === 0) return <div className="text-xs text-zinc-500 py-2 font-mono" data-testid={`${testid}-empty`}>No resign/reinstate actions recorded.</div>;
  return (
    <div className="overflow-x-auto mt-2" data-testid={testid}>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-zinc-500 font-mono uppercase border-b border-zinc-200">
          <th className="py-1 pr-3">When</th>{showWorker && <th className="py-1 pr-3">Worker</th>}<th className="py-1 pr-3">Action</th><th className="py-1 pr-3">By</th><th className="py-1">Reason</th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-zinc-100 last:border-0" data-testid={`audit-row-${r.id}`}>
            <td className="py-1 pr-3 font-mono whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
            {showWorker && <td className="py-1 pr-3">{r.worker_name}</td>}
            <td className={`py-1 pr-3 uppercase font-mono ${r.action === "resigned" ? "text-amber-700" : "text-emerald-700"}`}>{r.action}</td>
            <td className="py-1 pr-3">{r.by_name}</td>
            <td className="py-1 text-zinc-600">{r.reason || "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function AllowancesEditor({ value, onChange, testPrefix = "" }) {
  const list = value || [];
  const upd = (idx, patch) => onChange(list.map((a, i) => i === idx ? { ...a, ...patch } : a));
  const add = () => onChange([...list, { ...BLANK_ALLOWANCE }]);
  const rm = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase font-mono text-zinc-500">Allowances (fixed monthly)</div>
        <button type="button" onClick={add} className="text-xs text-[#002FA7] flex items-center gap-1" data-testid={`add-allowance-${testPrefix}`}><Plus size={12} /> Add allowance</button>
      </div>
      {list.length === 0 && <div className="text-xs text-zinc-400">No allowances. Click "Add allowance".</div>}
      {list.map((a, i) => (
        <div key={i} className="flex gap-2 mb-1">
          <input placeholder="Label (e.g. Transport)" value={a.label} onChange={(e) => upd(i, { label: e.target.value })} className="flex-1 border border-zinc-200 px-2 py-1 text-sm" data-testid={`allowance-label-${testPrefix}-${i}`} />
          <input type="number" step="0.01" placeholder="Amount" value={a.amount} onChange={(e) => upd(i, { amount: e.target.value })} className="w-28 border border-zinc-200 px-2 py-1 text-sm font-mono" data-testid={`allowance-amount-${testPrefix}-${i}`} />
          <button type="button" onClick={() => rm(i)} className="text-red-700 hover:bg-red-50 px-2"><X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function WorkerEditor({ worker, onCancel, onSaved }) {
  const [f, setF] = useState({
    name: worker.name || "",
    phone: worker.phone || "",
    ic_no: worker.ic_no || "",
    basic_salary: worker.basic_salary ?? 0,
    epf_label:    worker.epf_label    ?? "EPF",
    epf_amount:   worker.epf_amount   ?? "",
    epf_employer_amount:   worker.epf_employer_amount   ?? "",
    socso_label:  worker.socso_label  ?? "SOCSO",
    socso_amount: worker.socso_amount ?? "",
    socso_employer_amount: worker.socso_employer_amount ?? "",
    sip_label:    worker.sip_label    ?? "SIP",
    sip_amount:   worker.sip_amount   ?? "",
    sip_employer_amount:   worker.sip_employer_amount   ?? "",
    extra1_label: worker.extra1_label ?? "",
    extra1_amount:worker.extra1_amount ?? "",
    extra1_employer_amount:worker.extra1_employer_amount ?? "",
    extra2_label: worker.extra2_label ?? "",
    extra2_amount:worker.extra2_amount ?? "",
    extra2_employer_amount:worker.extra2_employer_amount ?? "",
    allowances: worker.allowances || [],
    password: "",
  });
  const save = async () => {
    try {
      const payload = {
        ...f,
        pay_type: f.pay_type || "salary",
        basic_salary: Number(f.basic_salary) || 0,
        hourly_rate: Number(f.hourly_rate) || 0,
        per_trip_rate: Number(f.per_trip_rate) || 0,
        epf_label: (f.epf_label || "").trim(),
        epf_amount: f.epf_amount === "" ? null : Number(f.epf_amount),
        epf_employer_amount: f.epf_employer_amount === "" ? null : Number(f.epf_employer_amount),
        socso_label: (f.socso_label || "").trim(),
        socso_amount: f.socso_amount === "" ? null : Number(f.socso_amount),
        socso_employer_amount: f.socso_employer_amount === "" ? null : Number(f.socso_employer_amount),
        sip_label: (f.sip_label || "").trim(),
        sip_amount: f.sip_amount === "" ? null : Number(f.sip_amount),
        sip_employer_amount: f.sip_employer_amount === "" ? null : Number(f.sip_employer_amount),
        extra1_label: (f.extra1_label || "").trim(),
        extra1_amount: f.extra1_amount === "" ? null : Number(f.extra1_amount),
        extra1_employer_amount: f.extra1_employer_amount === "" ? null : Number(f.extra1_employer_amount),
        extra2_label: (f.extra2_label || "").trim(),
        extra2_amount: f.extra2_amount === "" ? null : Number(f.extra2_amount),
        extra2_employer_amount: f.extra2_employer_amount === "" ? null : Number(f.extra2_employer_amount),
        allowances: (f.allowances || []).filter((a) => a.label).map((a) => ({ label: a.label, amount: Number(a.amount) || 0 })),
      };
      if (!payload.password) delete payload.password;
      await api.patch(`/punch/workers/${worker.id}`, payload);
      toast.success("Worker updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-3" data-testid={`worker-editor-${worker.id}`}>
      <div className="text-xs font-mono uppercase text-[#002FA7] mb-2">Edit {worker.email}</div>
      <div className="grid md:grid-cols-3 gap-2">
        <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" />
        <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" />
        <input placeholder="IC No" value={f.ic_no} onChange={(e) => setF({ ...f, ic_no: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm" />
        <input type="password" placeholder="Reset password (optional)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="border border-zinc-200 px-3 py-2 text-sm md:col-span-3" />
      </div>
      <PayTypeEditor value={f} onChange={(patch) => setF({ ...f, ...patch })} testPrefix={`edit-${worker.id}`} />
      <StatutoryEditor value={f} onChange={(patch) => setF({ ...f, ...patch })} testPrefix={`edit-${worker.id}`} />
      <AllowancesEditor value={f.allowances} onChange={(a) => setF({ ...f, allowances: a })} testPrefix={`edit-${worker.id}`} />
      <div className="flex gap-2 mt-3">
        <button onClick={save} className="bg-emerald-600 text-white text-sm px-3 py-1.5 flex items-center gap-1" data-testid={`save-worker-${worker.id}`}><FloppyDisk size={14} /> Save</button>
        <button onClick={onCancel} className="text-sm text-zinc-600 px-3 py-1.5 border border-zinc-200 flex items-center gap-1"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

function PayTypeEditor({ value, onChange, testPrefix = "" }) {
  const pt = value.pay_type || "salary";
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      <div className="text-[10px] uppercase font-mono text-zinc-500 mb-2">Pay type</div>
      <div className="flex gap-2 flex-wrap mb-2" role="tablist">
        {PAY_TYPES.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange({ pay_type: opt.key })}
            className={`px-3 py-1.5 text-xs border transition-colors ${pt === opt.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7]"}`}
            data-testid={`paytype-${testPrefix}-${opt.key}`}
            title={opt.hint}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-2 items-center">
        {pt === "salary" && (
          <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2 md:col-span-2">
            <span className="text-zinc-500 text-xs">RM Basic / month</span>
            <input type="number" step="0.01" value={value.basic_salary ?? 0} onChange={(e) => onChange({ basic_salary: e.target.value })} className="w-full font-mono text-sm outline-none" data-testid={`paytype-basic-${testPrefix}`} />
          </label>
        )}
        {pt === "hourly" && (
          <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2 md:col-span-2">
            <span className="text-zinc-500 text-xs">RM / hour</span>
            <input type="number" step="0.01" value={value.hourly_rate ?? 0} onChange={(e) => onChange({ hourly_rate: e.target.value })} className="w-full font-mono text-sm outline-none" data-testid={`paytype-hourly-${testPrefix}`} />
          </label>
        )}
        {pt === "per_trip" && (
          <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2 md:col-span-2">
            <span className="text-zinc-500 text-xs">RM / trip</span>
            <input type="number" step="0.01" value={value.per_trip_rate ?? 0} onChange={(e) => onChange({ per_trip_rate: e.target.value })} className="w-full font-mono text-sm outline-none" data-testid={`paytype-trip-${testPrefix}`} />
          </label>
        )}
        <div className="text-[10px] text-zinc-400 md:col-span-1">
          {PAY_TYPES.find((o) => o.key === pt)?.hint}
        </div>
      </div>
    </div>
  );
}

function StatutoryEditor({ value, onChange, testPrefix = "" }) {
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase font-mono text-zinc-500">Statutory deductions (manual RM figures per KWSP/SOCSO/EIS table)</div>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-1 items-center text-[10px] uppercase font-mono text-zinc-400 mb-1">
        <span>Label</span>
        <span className="w-28 text-center">Employee RM</span>
        <span className="w-28 text-center">Employer RM</span>
      </div>
      {STATUTORY_SLOTS.map(({ key, defaultLabel }) => (
        <div key={key} className="grid grid-cols-[1fr_auto_auto] gap-1 items-center mb-1">
          <input
            placeholder={defaultLabel || "Extra label"}
            value={value[`${key}_label`] ?? ""}
            onChange={(e) => onChange({ [`${key}_label`]: e.target.value })}
            className="border border-zinc-200 px-2 py-1 text-xs uppercase"
            data-testid={`stat-label-${testPrefix}-${key}`}
          />
          <div className="flex items-center border border-zinc-200 px-2 py-1 w-28">
            <span className="text-[10px] text-zinc-400 mr-1 font-mono">RM</span>
            <input
              type="number" step="0.01"
              placeholder="0.00"
              value={value[`${key}_amount`] ?? ""}
              onChange={(e) => onChange({ [`${key}_amount`]: e.target.value })}
              className="w-full text-xs font-mono outline-none"
              data-testid={`stat-amount-${testPrefix}-${key}`}
            />
          </div>
          <div className="flex items-center border border-zinc-200 px-2 py-1 w-28 bg-zinc-50">
            <span className="text-[10px] text-zinc-400 mr-1 font-mono">RM</span>
            <input
              type="number" step="0.01"
              placeholder="0.00"
              value={value[`${key}_employer_amount`] ?? ""}
              onChange={(e) => onChange({ [`${key}_employer_amount`]: e.target.value })}
              className="w-full text-xs font-mono outline-none bg-transparent"
              data-testid={`stat-emp-amount-${testPrefix}-${key}`}
            />
          </div>
        </div>
      ))}
      <div className="text-[10px] text-zinc-400 mt-1">Enter exact RM for Employee & Employer (leave blank to disable slot). No % calculation.</div>
    </div>
  );
}

/* ---------------- Shifts ---------------- */
function ShiftsTab() {
  const [shifts, setShifts] = useState([]);
  const load = async () => { try { const { data } = await api.get("/punch/shifts"); setShifts(data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); }, []);
  const save = async (s) => { try { await api.post("/punch/shifts", s); toast.success(`Shift ${s.code} saved`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (code) => { if (!window.confirm(`Delete shift ${code}?`)) return; try { await api.delete(`/punch/shifts/${code}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="bg-white border border-zinc-200 p-4">
      <div className="font-display font-black text-lg mb-3">Shifts (rates editable — click Save on any row)</div>
      <div className="text-xs text-zinc-500 mb-3">Shift <b>N (Normal Working)</b> is the regular working-day baseline. Late penalties are computed from this shift's start time.</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-200"><tr>
            <th className="text-left px-2 py-2">Code</th><th className="text-left px-2 py-2">Name</th><th className="text-left px-2 py-2">Start</th><th className="text-left px-2 py-2">End</th><th className="text-left px-2 py-2">Rate (RM)</th><th className="text-left px-2 py-2">OT</th><th className="text-right px-2 py-2"></th>
          </tr></thead>
          <tbody>
            {shifts.map((s) => (
              <ShiftRow key={s.code} row={s} onSave={save} onDelete={del} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 border-t border-zinc-200 pt-4">
        <div className="text-[10px] uppercase font-mono text-zinc-500 mb-2">Add new shift</div>
        <ShiftRow row={{ code: "", name: "", start: "08:00", end: "12:00", rate: 20, is_overtime: false }} onSave={save} isNew />
      </div>
    </div>
  );
}

function ShiftRow({ row, onSave, onDelete, isNew }) {
  const [s, setS] = useState(row);
  const upd = (k) => (e) => setS({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) });
  return (
    <tr className="border-b border-zinc-100" data-testid={`shift-row-${row.code || "new"}`}>
      <td className="px-2 py-2"><input value={s.code} onChange={upd("code")} className="w-14 border border-zinc-200 px-2 py-1 font-mono text-xs uppercase" data-testid={`shift-code-${row.code || "new"}`} /></td>
      <td className="px-2 py-2"><input value={s.name} onChange={upd("name")} className="w-40 border border-zinc-200 px-2 py-1 text-xs" /></td>
      <td className="px-2 py-2"><input type="time" value={s.start} onChange={upd("start")} className="border border-zinc-200 px-2 py-1 font-mono text-xs" /></td>
      <td className="px-2 py-2"><input type="time" value={s.end} onChange={upd("end")} className="border border-zinc-200 px-2 py-1 font-mono text-xs" /></td>
      <td className="px-2 py-2"><input type="number" step="0.01" value={s.rate} onChange={upd("rate")} className="w-20 border border-zinc-200 px-2 py-1 font-mono text-xs" data-testid={`shift-rate-${row.code || "new"}`} /></td>
      <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!s.is_overtime} onChange={upd("is_overtime")} className="accent-[#FF4F00]" /></td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        <button onClick={() => onSave(s)} className="text-xs bg-[#002FA7] text-white px-2 py-1 mr-1" data-testid={`save-shift-${row.code || "new"}`}>{isNew ? "Add" : "Save"}</button>
        {!isNew && <button onClick={() => onDelete(row.code)} className="text-xs text-red-700 hover:bg-red-50 p-1"><Trash size={12} /></button>}
      </td>
    </tr>
  );
}

/* ---------------- Company Settings ---------------- */
const DEFAULT_LATE_SCHEDULE = [
  { from_min: 10,  penalty: 5,  label: "Late 07:40–<08:00" },
  { from_min: 30,  penalty: 10, label: "Late 08:00–<08:30" },
  { from_min: 60,  penalty: 15, label: "Late 08:30–<09:00" },
  { from_min: 90,  penalty: 20, label: "Late 09:00–<09:30" },
  { from_min: 120, penalty: 25, label: "Late 09:30–<10:00" },
  { from_min: 150, penalty: 30, label: "Late 10:00–<11:00" },
  { from_min: 210, penalty: 35, label: "Late ≥11:00" },
];
function CompanyTab() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/punch/company").then(({ data }) => setF(data || {})); }, []);
  const save = async () => {
    try {
      const sched = (f.late_penalty_schedule || []).map(r => ({
        from_min: Number(r.from_min) || 0,
        penalty: Number(r.penalty) || 0,
        label: String(r.label || "Late"),
      })).sort((a,b) => a.from_min - b.from_min);
      const payload = {
        ...f,
        working_days_per_month: Number(f.working_days_per_month) || 26,
        daily_wage_add: Number(f.daily_wage_add ?? 10),
        late_penalty_schedule: sched.length ? sched : null,
      };
      const { data } = await api.put("/punch/company", payload);
      setF(data);
      toast.success("Company settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!f) return <div className="text-sm text-zinc-500">Loading…</div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const schedule = f.late_penalty_schedule && f.late_penalty_schedule.length
    ? f.late_penalty_schedule
    : DEFAULT_LATE_SCHEDULE;
  const setRule = (i, k, v) => {
    const next = [...schedule];
    next[i] = { ...next[i], [k]: v };
    setF({ ...f, late_penalty_schedule: next });
  };
  const addRule = () => {
    const next = [...schedule, { from_min: 0, penalty: 0, label: "New rule" }];
    setF({ ...f, late_penalty_schedule: next });
  };
  const removeRule = (i) => {
    const next = schedule.filter((_, idx) => idx !== i);
    setF({ ...f, late_penalty_schedule: next });
  };
  const resetSchedule = () => {
    setF({ ...f, late_penalty_schedule: DEFAULT_LATE_SCHEDULE.map(r => ({...r})) });
  };
  // Live preview: compute IN-time label for each rule (baseline + from_min).
  const baselineMin = (() => {
    const s = f.normal_shift_start || "07:30";
    const [h, m] = s.split(":").map(Number);
    return (h||0)*60 + (m||0);
  })();
  const minToHHMM = (mins) => {
    const t = Math.max(0, Math.floor(mins));
    const h = Math.floor(t/60), m = t%60;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  };
  return (
    <div className="bg-white border border-zinc-200 p-4 max-w-3xl">
      <div className="font-display font-black text-lg mb-3">Company / Payslip Header</div>
      <div className="grid md:grid-cols-2 gap-2">
        <input value={f.name || ""} onChange={set("name")} placeholder="Company name" className="border border-zinc-200 px-3 py-2 text-sm md:col-span-2" data-testid="company-name" />
        <input value={f.reg_no || ""} onChange={set("reg_no")} placeholder="Registration No" className="border border-zinc-200 px-3 py-2 text-sm" data-testid="company-reg" />
        <input value={f.phone || ""} onChange={set("phone")} placeholder="Phone" className="border border-zinc-200 px-3 py-2 text-sm" />
        <input value={f.email || ""} onChange={set("email")} placeholder="Email" className="border border-zinc-200 px-3 py-2 text-sm md:col-span-2" />
        <input value={f.address_line1 || ""} onChange={set("address_line1")} placeholder="Address line 1" className="border border-zinc-200 px-3 py-2 text-sm md:col-span-2" data-testid="company-addr1" />
        <input value={f.address_line2 || ""} onChange={set("address_line2")} placeholder="Address line 2" className="border border-zinc-200 px-3 py-2 text-sm md:col-span-2" />
        <input value={f.address_line3 || ""} onChange={set("address_line3")} placeholder="Address line 3" className="border border-zinc-200 px-3 py-2 text-sm md:col-span-2" />
        <input value={f.prepared_by || ""} onChange={set("prepared_by")} placeholder="Prepared by (name)" className="border border-zinc-200 px-3 py-2 text-sm" />
        <input value={f.approved_by || ""} onChange={set("approved_by")} placeholder="Approved by (name)" className="border border-zinc-200 px-3 py-2 text-sm" />
        <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2">
          <span className="text-zinc-500 text-xs">Normal Start</span>
          <input type="time" value={f.normal_shift_start || "07:30"} onChange={set("normal_shift_start")} className="font-mono outline-none w-full" data-testid="company-normal-start" />
        </label>
        <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2">
          <span className="text-zinc-500 text-xs">Working days / month</span>
          <input type="number" step="0.5" value={f.working_days_per_month ?? 26} onChange={set("working_days_per_month")} className="font-mono outline-none w-full" />
        </label>
        <label className="border border-zinc-200 px-3 py-2 text-sm flex items-center gap-2">
          <span className="text-zinc-500 text-xs">Daily wage + RM</span>
          <input type="number" step="0.5" value={f.daily_wage_add ?? 10} onChange={set("daily_wage_add")} className="font-mono outline-none w-full" data-testid="company-daily-add" />
        </label>
      </div>

      {/* ---------------- Late Penalty Schedule Editor ---------------- */}
      <div className="mt-6 border-t border-zinc-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-display font-black text-base">Late Penalty Rules</div>
            <div className="text-xs text-zinc-500">Minutes counted from Normal Start ({f.normal_shift_start || "07:30"}). The highest rule whose "Minutes late ≥" is met wins. Anything under the smallest rule = on time (no penalty).</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={resetSchedule} className="text-[11px] uppercase border border-zinc-300 px-2 py-1 hover:bg-zinc-100" data-testid="late-reset" title="Restore the default 7-tier schedule">Reset default</button>
            <button onClick={addRule} className="text-[11px] uppercase border border-[#002FA7] text-[#002FA7] px-2 py-1 hover:bg-[#002FA7] hover:text-white flex items-center gap-1" data-testid="late-add"><Plus size={12} /> Add rule</button>
          </div>
        </div>
        <table className="w-full text-sm border border-zinc-200" data-testid="late-schedule">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-2 py-1 w-32">Minutes late ≥</th>
              <th className="text-left px-2 py-1 w-32">→ IN time</th>
              <th className="text-left px-2 py-1 w-32">RM Penalty</th>
              <th className="text-left px-2 py-1">Label (shown on slip)</th>
              <th className="w-10 px-1"></th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((r, i) => (
              <tr key={i} className="border-t border-zinc-200" data-testid={`late-row-${i}`}>
                <td className="px-2 py-1"><input type="number" step="1" min="0" value={r.from_min ?? 0} onChange={(e) => setRule(i, "from_min", e.target.value)} className="w-20 border border-zinc-200 px-2 py-1 font-mono text-right" data-testid={`late-row-${i}-min`} /></td>
                <td className="px-2 py-1 font-mono text-zinc-500">{minToHHMM(baselineMin + (Number(r.from_min) || 0))}</td>
                <td className="px-2 py-1"><div className="flex items-center gap-1"><span className="text-zinc-400 text-xs">RM</span><input type="number" step="0.5" min="0" value={r.penalty ?? 0} onChange={(e) => setRule(i, "penalty", e.target.value)} className="w-20 border border-zinc-200 px-2 py-1 font-mono text-right" data-testid={`late-row-${i}-penalty`} /></div></td>
                <td className="px-2 py-1"><input value={r.label ?? ""} onChange={(e) => setRule(i, "label", e.target.value)} className="w-full border border-zinc-200 px-2 py-1" placeholder="e.g. Late 08:00–<08:30" data-testid={`late-row-${i}-label`} /></td>
                <td className="px-1"><button onClick={() => removeRule(i)} className="text-red-600 hover:bg-red-50 p-1" title="Remove this rule" data-testid={`late-row-${i}-remove`}><Trash size={14} /></button></td>
              </tr>
            ))}
            {schedule.length === 0 && (
              <tr><td colSpan={5} className="text-center text-xs text-zinc-500 py-3">No rules — every day counts as on time.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <button onClick={save} className="mt-4 bg-[#002FA7] text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="save-company"><FloppyDisk size={14} /> Save</button>
    </div>
  );
}

/* ---------------- Payroll ---------------- */
function PayrollTab() {
  const [workers, setWorkers] = useState([]);
  const [wid, setWid] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [slip, setSlip] = useState(null);
  const [autoLoad, setAutoLoad] = useState(0);
  useEffect(() => { api.get("/punch/workers").then(({ data }) => { const act = data.filter(w => !w.is_resigned); setWorkers(act); if (act[0]) setWid(act[0].id); }); }, []);
  const load = async () => { try { const { data } = await api.get("/punch/salary", { params: { worker_id: wid, date_from: from, date_to: to } }); setSlip(data); } catch (e) { toast.error(formatApiError(e)); } };
  // Auto-load when a saved slip is picked
  useEffect(() => { if (autoLoad && wid && from && to) load(); /* eslint-disable-next-line */ }, [autoLoad]);

  const pickSaved = (row) => {
    setWid(row.worker_id);
    // Derive from/to from `month` YYYY-MM: full-month window.
    const [y, m] = row.month.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2, "0")}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    setFrom(first);
    setTo(last);
    setAutoLoad((x) => x + 1);
    toast.success(`Loaded ${row.worker_name} · ${row.month}`);
  };

  return (
    <div>
      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap gap-3 items-end print:hidden">
        <select value={wid} onChange={(e) => setWid(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm" data-testid="payroll-worker">
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="payroll-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="payroll-to" />
        <button onClick={load} className="bg-[#002FA7] text-white text-sm px-4 py-2" data-testid="load-slip-btn">Generate</button>
        {slip && <button onClick={() => window.print()} className="bg-emerald-600 text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="print-slip-btn"><Printer size={14} /> Print / Save PDF</button>}
      </div>
      <SavedSlipsBrowser workers={workers} onPick={pickSaved} />
      {slip && <Payslip slip={slip} onSavedChange={() => setAutoLoad((x) => x + 1)} />}
    </div>
  );
}

function SavedSlipsBrowser({ workers, onPick }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ worker_id: "", month_from: "", month_to: "", q: "" });
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/punch/slip-edits", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open && items.length === 0) load(); /* eslint-disable-next-line */ }, [open]);

  return (
    <div className="bg-white border border-zinc-200 mb-4 print:hidden" data-testid="saved-slips-browser">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-zinc-50" data-testid="toggle-saved-slips">
        <span className="font-display font-black uppercase text-sm flex items-center gap-2">
          <MagnifyingGlass size={14} /> Saved slips ({items.length})
        </span>
        <span className="text-xs text-zinc-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-200 p-3">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <select value={f.worker_id} onChange={(e) => setF({ ...f, worker_id: e.target.value })} className="border border-zinc-200 px-2 py-1 text-sm" data-testid="saved-filter-worker">
              <option value="">All workers</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <label className="border border-zinc-200 px-2 py-1 text-xs flex items-center gap-1">
              <span className="text-zinc-500">Month from</span>
              <input type="month" value={f.month_from} onChange={(e) => setF({ ...f, month_from: e.target.value })} className="font-mono outline-none" data-testid="saved-filter-from" />
            </label>
            <label className="border border-zinc-200 px-2 py-1 text-xs flex items-center gap-1">
              <span className="text-zinc-500">Month to</span>
              <input type="month" value={f.month_to} onChange={(e) => setF({ ...f, month_to: e.target.value })} className="font-mono outline-none" data-testid="saved-filter-to" />
            </label>
            <input placeholder="Search name / notes…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} className="border border-zinc-200 px-2 py-1 text-sm flex-1 min-w-[180px]" data-testid="saved-search" />
            <button onClick={load} className="bg-[#002FA7] text-white text-xs px-3 py-1.5 flex items-center gap-1" data-testid="saved-search-btn">
              <MagnifyingGlass size={12} /> Search
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-2 py-1">Month</th>
                  <th className="text-left px-2 py-1">Worker</th>
                  <th className="text-left px-2 py-1">Voucher label</th>
                  <th className="text-left px-2 py-1">Notes</th>
                  <th className="text-left px-2 py-1">Updated</th>
                  <th className="text-right px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-4 text-zinc-500">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-4 text-zinc-400">No saved slips.</td></tr>
                ) : items.map((row) => (
                  <tr key={`${row.worker_id}-${row.month}`} className="border-b border-zinc-100" data-testid={`saved-slip-${row.worker_id}-${row.month}`}>
                    <td className="px-2 py-1 font-mono text-xs">{row.month}</td>
                    <td className="px-2 py-1">{row.worker_name || row.worker_id}</td>
                    <td className="px-2 py-1 font-mono text-xs">{row.voucher_month || "—"}</td>
                    <td className="px-2 py-1 text-xs text-zinc-600 truncate max-w-[240px]">{row.notes || "—"}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-zinc-500">{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => onPick(row)} className="text-xs bg-emerald-600 text-white px-2 py-1" data-testid={`load-saved-${row.worker_id}-${row.month}`}>Load & edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* Editable payslip — mirrors MAN.pdf layout, all fields editable in place (also editable in print). */
function Payslip({ slip, onSavedChange }) {
  const [c, setC] = useState(slip.company || {});
  useEffect(() => { setC(slip.company || {}); }, [slip.company]);
  // Editable OT lines (admin may override rate/hours before printing)
  const [otLines, setOtLines] = useState(slip.overtime.lines);
  const [extraNote, setExtraNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const autoMonthLabel = () => {
    const d = new Date(slip.period.to || Date.now());
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }).toUpperCase();
  };
  const [voucherMonth, setVoucherMonth] = useState(autoMonthLabel);
  const monthKey = (slip.period.to || slip.period.from || new Date().toISOString().slice(0, 10)).slice(0, 7);
  const autoSundays = slip.public_holidays?.sundays || 0;
  const autoStats = {
    ot_entries: slip.overtime.lines.length,
    late: slip.attendance.counts.late,
    half_day: slip.attendance.counts.half_day,
    full_leave: slip.attendance.counts.full_leave,
    public_holiday: autoSundays,
  };
  const [stats, setStats] = useState(autoStats);
  const setStat = (k, v) => { setStats((s) => ({ ...s, [k]: v === "" ? 0 : Number(v) })); setDirty(true); };

  // Load persisted edits when slip changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const auto = {
        ot_entries: slip.overtime.lines.length,
        late: slip.attendance.counts.late,
        half_day: slip.attendance.counts.half_day,
        full_leave: slip.attendance.counts.full_leave,
        public_holiday: slip.public_holidays?.sundays || 0,
      };
      try {
        const { data } = await api.get(`/punch/slip-edits/${slip.worker.id}`, { params: { month: monthKey } });
        if (cancelled) return;
        setOtLines(data.ot_lines != null ? data.ot_lines : slip.overtime.lines);
        setExtraNote(data.notes || "");
        setVoucherMonth(data.voucher_month || autoMonthLabel());
        setStats({ ...auto, ...(data.stat_overrides || {}) });
        setDirty(false);
      } catch {
        setOtLines(slip.overtime.lines);
        setStats(auto);
        setDirty(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slip.worker.id, slip.period.from, slip.period.to]);

  const otTotal = otLines.reduce((s, l) => s + (Number(l.rate) || 0), 0);

  const saveEdits = async () => {
    try {
      await api.put("/punch/slip-edits", {
        worker_id: slip.worker.id,
        month: monthKey,
        voucher_month: voucherMonth,
        notes: extraNote,
        ot_lines: otLines.map((l) => ({
          timestamp: l.timestamp || new Date().toISOString(),
          shift_code: l.shift_code || "",
          description: l.description || "",
          rate: Number(l.rate) || 0,
        })),
        stat_overrides: stats,
      });
      setDirty(false);
      toast.success(`Slip saved for ${monthKey}`);
      if (onSavedChange) onSavedChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const w = slip.worker;
  const a = slip.attendance;
  const ded = slip.deductions;
  const emp = slip.employer;
  const rm = (n) => `RM ${Number(n || 0).toFixed(2)}`;
  const basic = Number(w.basic_salary || 0);
  const allowanceTotal = slip.allowances.total;
  const statutoryTotal = (ded.statutory_employee_total != null)
    ? Number(ded.statutory_employee_total)
    : (ded.epf_employee + ded.socso_employee + ded.sip_employee);

  // Salary Voucher (paid on 1st): Basic − statutory deductions only.
  const salaryNet = +(basic - statutoryTotal).toFixed(2);

  // ---- Payment-Summary amounts (single-input boxes · count × auto rate).
  // D=OT count × auto per-entry rate (from server OT lines average).
  // E=Late Charges — single RM total (no per-day).
  // F/G/H=count × auto per-day rate derived from daily wage.
  const workingDays = a.working_days_per_month || 26;
  const dailyWage = workingDays > 0 ? basic / workingDays : 0;
  const dailyWageAdd = a.daily_wage_add ?? 10;
  // OT per-entry rate uses FRESH server data (never saved ot_lines which may be zeroed).
  const freshOTCount = (slip.overtime && slip.overtime.lines) ? slip.overtime.lines.length : 0;
  const freshOTTotal = slip.overtime ? Number(slip.overtime.total || 0) : 0;
  const otPerEntry = freshOTCount > 0 && freshOTTotal > 0 ? +(freshOTTotal / freshOTCount).toFixed(2) : 25;
  const halfRate = +(dailyWage / 2).toFixed(2);
  const fullRate = +dailyWage.toFixed(2);
  const phRate   = +(dailyWage + dailyWageAdd).toFixed(2);

  const cnt = (k) => Number(stats[k]) || 0;

  const effOT   = +(cnt("ot_entries") * otPerEntry).toFixed(2);
  const effLate = +(Number(stats.late_amount) || 0).toFixed(2);
  const effHalf = +(cnt("half_day") * halfRate).toFixed(2);
  const effFull = +(cnt("full_leave") * fullRate).toFixed(2);
  const effPH   = +(cnt("public_holiday") * phRate).toFixed(2);

  const setRMStat = (k, v) => { setStats((s) => ({ ...s, [k]: v === "" ? 0 : v })); setDirty(true); };

  // OT Slip (paid on 15th): Allowances + OT − late charges − half-day − full-day + PH bonus.
  const otSlipNet = +(allowanceTotal + effOT - effLate - effHalf - effFull + effPH).toFixed(2);

  // Combined figure (kept for backwards references)
  const totalDeductions = statutoryTotal + effLate + effHalf + effFull;
  const netPay = basic + allowanceTotal + otTotal - totalDeductions;

  const setCompany = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setOtLine = (i, k, v) => { setOtLines(otLines.map((l, idx) => idx === i ? { ...l, [k]: v } : l)); setDirty(true); };
  const changeNote = (v) => { setExtraNote(v); setDirty(true); };
  const changeVoucherMonth = (v) => { setVoucherMonth(v); setDirty(true); };

  // Print CSS: hide input borders in print, show plain text.
  return (
    <div id="payslip-root" className="space-y-4 print:space-y-0">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .payslip-input { border: none !important; padding: 0 !important; background: transparent !important; }
          .payslip-input:focus { outline: none !important; }
          .no-print { display: none !important; }
        }
        .payslip-input { background: transparent; }
      `}</style>

      {/* ---------------- SALARY VOUCHER ---------------- */}
      <div className="bg-white border-2 border-zinc-900 p-8 print:border print:break-inside-avoid" data-testid="payslip-salary">
        <div className="text-center border-b-2 border-zinc-900 pb-3">
          <EditableText value={c.name || ""} onChange={setCompany("name")} className="font-display font-black text-2xl tracking-wide text-center" data-testid="slip-company-name" />
          <div className="flex items-center justify-center gap-2 text-xs font-mono">
            <EditableText value={c.reg_no || ""} onChange={setCompany("reg_no")} className="text-center" />
          </div>
          <EditableText value={c.address_line1 || ""} onChange={setCompany("address_line1")} className="text-xs text-center block w-full" />
          <EditableText value={c.address_line2 || ""} onChange={setCompany("address_line2")} className="text-xs text-center block w-full" />
          <EditableText value={c.address_line3 || ""} onChange={setCompany("address_line3")} className="text-xs text-center block w-full" />
          <div className="flex items-center justify-center gap-4 text-[11px] mt-1">
            <div className="flex items-center gap-1"><span className="text-zinc-500">E-MAIL:</span><EditableText value={c.email || ""} onChange={setCompany("email")} className="font-mono" /></div>
            <div className="flex items-center gap-1"><span className="text-zinc-500">TEL:</span><EditableText value={c.phone || ""} onChange={setCompany("phone")} className="font-mono" /></div>
          </div>
        </div>

        <div className="text-center py-3 border-b border-zinc-300">
          <div className="font-display font-black text-2xl">
            SALARY VOUCHER{" "}
            <input value={voucherMonth} onChange={(e) => changeVoucherMonth(e.target.value)} className="payslip-input font-display font-black text-2xl uppercase text-center w-40 border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none" data-testid="voucher-month" />
          </div>
          <div className="text-[11px] font-mono text-zinc-600 mt-1">Period: {slip.period.from} → {slip.period.to}</div>
        </div>

        <div className="pt-4 pb-3 text-sm">
          <div className="flex gap-3 items-center mb-1">
            <span className="uppercase text-xs text-zinc-500 w-16">Pay to</span>
            <span className="font-display font-black text-lg uppercase">{w.name}</span>
          </div>
          <div className="flex gap-3 items-center">
            <span className="uppercase text-xs text-zinc-500 w-16">IC No</span>
            <span className="font-mono">{w.ic_no || "—"}</span>
          </div>
        </div>

        {/* Two-column earnings/employer — SALARY VOUCHER shows ONLY Basic + Statutory (paid 1st of month) */}
        <div className="grid grid-cols-2 gap-6 border-t border-zinc-300 pt-4">
          <div>
            <table className="w-full text-sm">
              <tbody>
                <SlipRow
                  label={
                    w.pay_type === "hourly" ? `Basic Pay (${w.hours_worked ?? 0} hrs × RM${Number(w.hourly_rate || 0).toFixed(2)})`
                    : w.pay_type === "per_trip" ? `Basic Pay (${w.trips_count ?? 0} trips × RM${Number(w.per_trip_rate || 0).toFixed(2)})`
                    : "Basic Pay"
                  }
                  value={rm(basic)}
                  bold
                />
                {(ded.statutory_employee || []).map((s, i) => (
                  <SlipRow key={s.key || i} label={`Employee ${s.label}`} value={`- ${rm(s.amount)}`} negative />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-900">
                  <td className="pt-2 uppercase font-mono text-xs">Total Employee</td>
                  <td className="pt-2 text-right font-display font-black">{rm(salaryNet)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div>
            <table className="w-full text-sm">
              <tbody>
                {(emp.statutory || []).length === 0 ? (
                  <>
                    <SlipRow label="Employer's - EPF" value={rm(emp.epf)} />
                    <SlipRow label="Employer's - SOCSO" value={rm(emp.socso)} />
                    <SlipRow label="Employer's - SIP" value={rm(emp.sip)} />
                  </>
                ) : (emp.statutory.map((s, i) => (
                  <SlipRow key={s.key || i} label={`Employer's - ${s.label}`} value={rm(s.amount)} />
                )))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-900">
                  <td className="pt-2 uppercase font-mono text-xs">Total Employer's</td>
                  <td className="pt-2 text-right font-display font-black">{rm(emp.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-4 border-t-2 border-zinc-900 pt-3 flex items-center justify-between">
          <div className="uppercase font-display font-black text-lg">GROSS PAY (1<sup className="text-xs">st</sup>)</div>
          <div className="font-display font-black text-2xl" data-testid="gross-pay">{rm(salaryNet + emp.total)}</div>
        </div>

        {/* Footer signatures */}
        <div className="grid grid-cols-2 gap-6 mt-8 text-xs">
          <div>
            <div className="flex gap-2 items-baseline mb-6"><span className="uppercase text-zinc-500 w-24">Prepared by</span><EditableText value={c.prepared_by || ""} onChange={setCompany("prepared_by")} className="font-medium" /></div>
            <div className="border-t border-zinc-400 pt-1 text-[10px] uppercase text-zinc-500">Signature</div>
          </div>
          <div>
            <div className="flex gap-2 items-baseline mb-1"><span className="uppercase text-zinc-500 w-24">Approved by</span><EditableText value={c.approved_by || ""} onChange={setCompany("approved_by")} className="font-medium" /></div>
            <div className="flex gap-2 items-baseline mb-1"><span className="uppercase text-zinc-500 w-24">Received by</span><span className="font-medium uppercase">{w.name}</span></div>
            <div className="border-t border-zinc-400 pt-1 text-[10px] uppercase text-zinc-500">Signature</div>
          </div>
        </div>
      </div>

      {/* ---------------- OT SLIP with remarks ---------------- */}
      <div className="bg-white border-2 border-zinc-900 p-8 print:border print:break-inside-avoid print:break-before-page" data-testid="payslip-ot">
        <div className="text-center border-b-2 border-zinc-900 pb-2 mb-4 relative">
          <div className="font-display font-black text-2xl">OVERTIME SLIP · {voucherMonth}</div>
          <div className="text-[11px] font-mono">{w.name} · {w.ic_no || "—"} · {slip.period.from} → {slip.period.to} · <span className="text-[#FF4F00]">Paid on 15th of the month</span></div>
          <button
            onClick={saveEdits}
            className={`no-print absolute right-0 top-0 text-xs px-3 py-1 flex items-center gap-1 ${dirty ? "bg-amber-600 text-white" : "bg-emerald-600 text-white"}`}
            data-testid="save-slip-btn"
            title="Save this slip so you can search & re-open it later"
          >
            <FloppyDisk size={12} /> {dirty ? "Save changes" : "Confirm & Save"}
          </button>
        </div>

        {/* Payment Summary — read-only mirror of the editable stat boxes below.
            Edit the D/E/F/G/H boxes to change any figure here. */}
        <div className="border-2 border-zinc-900 mb-4">
          <div className="bg-zinc-900 text-white text-xs uppercase font-mono px-3 py-1 tracking-wider">Payment Summary (paid 15th) · edit via boxes below</div>
          <table className="w-full text-sm">
            <tbody>
              {slip.allowances.items.map((al, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="px-3 py-1">{al.label}</td>
                  <td className="px-3 py-1 text-right font-mono">{rm(al.amount)}</td>
                </tr>
              ))}
              <tr className="border-b border-zinc-100">
                <td className="px-3 py-1">Overtime ({Number(stats.ot_entries) || 0} entries × RM{otPerEntry.toFixed(2)})</td>
                <td className="px-3 py-1 text-right font-mono" data-testid="pmt-ot-amount">{rm(effOT)}</td>
              </tr>
              <tr className="border-b border-zinc-100">
                <td className="px-3 py-1">Late Charges</td>
                <td className="px-3 py-1 text-right font-mono text-red-700" data-testid="pmt-late-amount">- {rm(effLate)}</td>
              </tr>
              <tr className="border-b border-zinc-100">
                <td className="px-3 py-1">Half-day ({Number(stats.half_day) || 0} × RM{halfRate.toFixed(2)})</td>
                <td className="px-3 py-1 text-right font-mono text-red-700" data-testid="pmt-half-amount">- {rm(effHalf)}</td>
              </tr>
              <tr className="border-b border-zinc-100">
                <td className="px-3 py-1">Full Leave ({Number(stats.full_leave) || 0} × RM{fullRate.toFixed(2)})</td>
                <td className="px-3 py-1 text-right font-mono text-red-700" data-testid="pmt-full-amount">- {rm(effFull)}</td>
              </tr>
              {(Number(stats.public_holiday) || 0) > 0 && (
                <tr className="border-b border-zinc-100">
                  <td className="px-3 py-1">Public Holiday ({Number(stats.public_holiday) || 0} × RM{phRate.toFixed(2)})</td>
                  <td className="px-3 py-1 text-right font-mono" data-testid="pmt-ph-amount">{rm(effPH)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                <td className="px-3 py-2 uppercase font-display font-black text-sm">GROSS PAY (15<sup className="text-[9px]">th</sup>)</td>
                <td className="px-3 py-2 text-right font-display font-black text-2xl" data-testid="ot-gross-pay">{rm(otSlipNet)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="grid grid-cols-5 gap-3 text-center mb-4">
          <SingleInputStatBox label="OT-entries (D)" cntKey="ot_entries" stats={stats} setRMStat={setRMStat} perRate={otPerEntry} total={effOT} testId="stat-ot" unit="entries" />
          <SingleInputStatBox label="Late Charges (E)" cntKey="late_amount" stats={stats} setRMStat={setRMStat} total={effLate} testId="stat-late" mode="rm" negative />
          <SingleInputStatBox label="Half-day (F)" cntKey="half_day" stats={stats} setRMStat={setRMStat} perRate={halfRate} total={effHalf} testId="stat-half" unit="times" negative />
          <SingleInputStatBox label="Full Leave (G)" cntKey="full_leave" stats={stats} setRMStat={setRMStat} perRate={fullRate} total={effFull} testId="stat-full" unit="days" negative />
          <SingleInputStatBox label="Public Holidays (H)" cntKey="public_holiday" stats={stats} setRMStat={setRMStat} perRate={phRate} total={effPH} testId="stat-ph" unit="days" />
        </div>
        <div className="text-[10px] text-zinc-500 mb-3 no-print">
          Tip: type just the <b>count</b> in D/F/G/H — the RM rate is auto-derived (OT rate = server average; Half-day = daily-wage ÷ 2; Full leave = daily wage; Public holiday = daily wage + RM{dailyWageAdd}). <b>Late Charges (E)</b> is a total RM figure — type it directly. Daily wage = basic ÷ {workingDays} = RM{dailyWage.toFixed(2)}. Refer to the <b>Summary</b> tab for actual month figures.
        </div>
        <div className="hidden">
          <StatBox label="Half-day leave" value={a.counts.half_day} />
          <StatBox label="Half-day leave" value={a.counts.half_day} />
          <StatBox label="Half-day leave" value={a.counts.half_day} />
          <StatBox label="Full-day leave" value={a.counts.full_leave} />
        </div>

        <table className="w-full text-sm mb-4">
          <thead className="text-[10px] uppercase font-mono text-zinc-500 border-b-2 border-zinc-900">
            <tr>
              <th className="text-left py-1">Date & Time</th>
              <th className="text-left py-1">Shift</th>
              <th className="text-left py-1">Description (editable)</th>
              <th className="text-right py-1">Amount (RM)</th>
              <th className="text-right py-1 no-print"></th>
            </tr>
          </thead>
          <tbody>
            {otLines.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-4 text-zinc-400">No overtime entries</td></tr>
            ) : otLines.map((l, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-1 font-mono text-xs">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="py-1 font-mono">{l.shift_code}</td>
                <td className="py-1">
                  <input value={l.description || ""} placeholder="e.g. Night loading" onChange={(e) => setOtLine(i, "description", e.target.value)} className="payslip-input w-full border border-dashed border-zinc-300 focus:border-[#002FA7] px-1 outline-none text-sm" data-testid={`ot-desc-${i}`} />
                </td>
                <td className="py-1 text-right">
                  <input type="number" step="0.01" value={l.rate} onChange={(e) => setOtLine(i, "rate", Number(e.target.value))} className="payslip-input w-24 border border-dashed border-zinc-300 focus:border-[#002FA7] px-1 outline-none text-right font-mono text-sm" data-testid={`ot-rate-${i}`} />
                </td>
                <td className="py-1 text-right no-print">
                  <button
                    onClick={() => {
                      const l0 = otLines[i] || {};
                      const half = +(Number(l0.rate || 0) * 0.5).toFixed(2);
                      const suffix = (l0.description || "").includes("½") ? "" : " ½ (early leave)";
                      setOtLines(otLines.map((l, idx) => idx === i ? { ...l, rate: half, description: (l.description || "") + suffix } : l));
                      setDirty(true);
                    }}
                    className="text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-800 px-1.5 py-0.5 mr-1 border border-amber-300"
                    data-testid={`ot-half-${i}`}
                    title="Halve rate — worker went home early during this OT shift"
                  >½</button>
                  <button onClick={() => { setOtLines(otLines.filter((_, idx) => idx !== i)); setDirty(true); }} className="text-red-700 hover:bg-red-50 px-1"><X size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-zinc-900">
              <td colSpan={3} className="pt-2 text-right uppercase font-mono text-xs">OT Total</td>
              <td className="pt-2 text-right font-display font-black text-lg">{rm(otTotal)}</td>
              <td className="no-print"></td>
            </tr>
          </tfoot>
        </table>
        <button onClick={() => { setOtLines([...otLines, { timestamp: new Date().toISOString(), shift_code: "MANUAL", description: "", rate: 0 }]); setDirty(true); }} className="no-print text-xs text-[#002FA7] flex items-center gap-1 mb-1" data-testid="add-ot-line"><Plus size={12} /> Add manual OT entry</button>
        <div className="no-print text-[10px] text-zinc-500 mb-4">Tip: click <span className="bg-amber-100 border border-amber-300 text-amber-800 px-1">½</span> on a row when the worker went home early during that OT shift (halves the rate), or <X className="inline" size={10} /> to remove the row entirely.</div>

        {/* Late/leave breakdown + editable notes (Remarks block removed — figures live in stat boxes above) */}
        <div className="border-t-2 border-zinc-900 pt-3">
          {/* Daily late breakdown */}
          {a.days.some((d) => d.status !== "on_time") && (
            <div className="mt-1">
              <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Late / Leave breakdown</div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-300">
                  <tr><th className="text-left py-0.5">Date</th><th className="text-left py-0.5">Punch-in</th><th className="text-left py-0.5">Status</th><th className="text-right py-0.5">Penalty</th></tr>
                </thead>
                <tbody>
                  {a.days.filter((d) => d.status !== "on_time").map((d, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="py-0.5 font-mono">{d.date}</td>
                      <td className="py-0.5 font-mono">{d.punch_in_time}</td>
                      <td className="py-0.5">{d.label}</td>
                      <td className="py-0.5 text-right font-mono">{d.penalty ? rm(d.penalty) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3">
            <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Notes (editable)</div>
            <textarea value={extraNote} onChange={(e) => changeNote(e.target.value)} rows={3} placeholder="Additional remarks…" className="payslip-input w-full border border-dashed border-zinc-300 focus:border-[#002FA7] px-2 py-1 outline-none text-sm" data-testid="ot-notes" />
          </div>
        </div>

        {/* ---- LOAN section (per-worker per-month) ---- */}
        <LoanBlock workerId={w.id} period={slip.period} />
      </div>
    </div>
  );
}

function LoanBlock({ workerId, period }) {
  // Month is derived from period.to (YYYY-MM)
  const month = (period.to || period.from || new Date().toISOString().slice(0, 10)).slice(0, 7);
  const [loan, setLoan] = useState(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    api.get(`/punch/loans/${workerId}`, { params: { month } })
      .then(({ data }) => { setLoan(data); setDirty(false); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [workerId, month]);

  if (!loan) return null;

  const effectivePrev = loan.prev_balance_override != null ? Number(loan.prev_balance_override) : Number(loan.prev_balance || 0);
  const total = +(effectivePrev + Number(loan.new_loan || 0) - Number(loan.repayment || 0)).toFixed(2);
  const rm = (n) => `RM ${Number(n || 0).toFixed(2)}`;

  const upd = (k, v) => { setLoan({ ...loan, [k]: v }); setDirty(true); };
  const save = async () => {
    try {
      const { data } = await api.put("/punch/loans", {
        worker_id: workerId, month,
        new_loan: Number(loan.new_loan) || 0,
        repayment: Number(loan.repayment) || 0,
        prev_balance_override: loan.prev_balance_override === "" || loan.prev_balance_override == null ? null : Number(loan.prev_balance_override),
        notes: loan.notes || "",
      });
      setLoan(data); setDirty(false);
      toast.success(`Loan saved · balance ${rm(data.total_balance)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border-t-2 border-zinc-900 pt-3 mt-4" data-testid="loan-block">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-black text-sm uppercase">Company Loan · {month}</div>
        <button onClick={save} className="no-print text-xs bg-[#002FA7] text-white px-3 py-1 flex items-center gap-1 disabled:opacity-40" disabled={!dirty} data-testid="save-loan-btn">
          <FloppyDisk size={12} /> Save loan
        </button>
      </div>
      <table className="w-full text-sm">
        <tbody>
          <LoanRow label={`Balance loan from last month (${prevMonth(month)})`}>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-zinc-400 text-xs no-print">auto {rm(loan.prev_balance)}</span>
              <input
                type="number" step="0.01"
                value={loan.prev_balance_override ?? ""}
                placeholder={String(loan.prev_balance ?? 0)}
                onChange={(e) => upd("prev_balance_override", e.target.value === "" ? null : e.target.value)}
                className="payslip-input w-28 border border-dashed border-zinc-300 focus:border-[#002FA7] px-1 outline-none text-right font-mono text-sm"
                data-testid="loan-prev-override"
              />
            </div>
          </LoanRow>
          <LoanRow label="New loan this month">
            <input type="number" step="0.01" value={loan.new_loan ?? 0} onChange={(e) => upd("new_loan", e.target.value)}
              className="payslip-input w-28 border border-dashed border-zinc-300 focus:border-[#002FA7] px-1 outline-none text-right font-mono text-sm" data-testid="loan-new" />
          </LoanRow>
          <LoanRow label="This month pay loan (repayment)">
            <input type="number" step="0.01" value={loan.repayment ?? 0} onChange={(e) => upd("repayment", e.target.value)}
              className="payslip-input w-28 border border-dashed border-zinc-300 focus:border-[#002FA7] px-1 outline-none text-right font-mono text-sm" data-testid="loan-repayment" />
          </LoanRow>
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-zinc-900">
            <td className="pt-2 uppercase font-mono text-xs">Total balance loan</td>
            <td className="pt-2 text-right font-display font-black text-lg" data-testid="loan-total">{rm(total)}</td>
          </tr>
        </tfoot>
      </table>
      <div className="mt-2">
        <input value={loan.notes || ""} onChange={(e) => upd("notes", e.target.value)} placeholder="Loan notes (e.g. reason, expected settlement date)"
          className="payslip-input w-full border border-dashed border-zinc-300 focus:border-[#002FA7] px-2 py-1 outline-none text-sm" data-testid="loan-notes" />
      </div>
      {dirty && <div className="no-print text-[11px] text-amber-700 mt-1">Unsaved changes — click Save loan to persist.</div>}
    </div>
  );
}

function SlipRow({ label, value, bold, negative }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className={`py-1 ${bold ? "font-medium" : ""}`}>{label}</td>
      <td className={`py-1 text-right font-mono ${negative ? "text-red-700" : ""} ${bold ? "font-bold" : ""}`}>{value}</td>
    </tr>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="border border-zinc-300 p-2">
      <div className="text-[9px] uppercase font-mono text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function EditableStatBox({ label, value, onChange, testId }) {
  return (
    <div className="border border-zinc-300 p-2">
      <div className="text-[9px] uppercase font-mono text-zinc-500">{label}</div>
      <input
        type="number" min="0" step="1"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className="payslip-input font-display font-black text-2xl w-full text-center border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function SingleInputStatBox({ label, cntKey, stats, setRMStat, perRate, total, testId, unit = "days", mode = "count", negative }) {
  const val = Number(stats[cntKey]) || 0;
  return (
    <div className="border border-zinc-300 p-2">
      <div className="text-[9px] uppercase font-mono text-zinc-500 leading-tight mb-1 min-h-[22px]">{label}</div>
      <div className="flex items-center gap-1 justify-center mb-1">
        {mode === "rm" && <span className={`text-[10px] font-mono ${negative ? "text-red-700" : "text-zinc-500"}`}>{negative ? "- RM" : "RM"}</span>}
        <input
          type="number" min="0" step={mode === "rm" ? "0.01" : "1"}
          value={val}
          onChange={(e) => setRMStat(cntKey, e.target.value)}
          className="payslip-input font-mono font-bold text-xl w-20 text-right border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none px-1"
          data-testid={`${testId}-count`}
        />
        {mode === "count" && <span className="text-[9px] text-zinc-400 whitespace-nowrap">{unit}</span>}
      </div>
      {mode === "count" && (
        <div className="text-[9px] font-mono text-zinc-400 mb-0.5 no-print">× RM{Number(perRate || 0).toFixed(2)} auto</div>
      )}
      <div className={`font-display font-black text-lg ${negative ? "text-red-700" : ""}`} data-testid={`${testId}-total`}>
        {negative ? "- " : ""}RM {Number(total || 0).toFixed(2)}
      </div>
    </div>
  );
}

function ManualRateStatBox({ label, cntKey, rateKey, stats, setRMStat, total, testId, unit = "days", negative }) {
  const count = Number(stats[cntKey]) || 0;
  const rate = Number(stats[rateKey]) || 0;
  return (
    <div className="border border-zinc-300 p-2">
      <div className="text-[9px] uppercase font-mono text-zinc-500 leading-tight mb-1">{label}</div>
      <div className="flex items-center gap-1 justify-center mb-1">
        <input
          type="number" min="0" step="1"
          value={count}
          onChange={(e) => setRMStat(cntKey, e.target.value)}
          className="payslip-input font-mono font-bold text-base w-10 text-right border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none px-1"
          data-testid={`${testId}-count`}
          title={unit}
        />
        <span className="text-[10px] text-zinc-400">×</span>
        <span className="text-[9px] text-zinc-400 no-print">RM</span>
        <input
          type="number" min="0" step="0.01"
          value={rate}
          onChange={(e) => setRMStat(rateKey, e.target.value)}
          className="payslip-input font-mono font-bold text-base w-16 text-right border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none px-1"
          data-testid={`${testId}-rate`}
          title={`RM per ${unit === "entries" ? "entry" : "day"}`}
        />
      </div>
      <div className={`font-display font-black text-lg ${negative ? "text-red-700" : ""}`} data-testid={`${testId}-total`}>
        {negative ? "- " : ""}RM {Number(total || 0).toFixed(2)}
      </div>
    </div>
  );
}

function ReadOnlyRMStat({ label, value, testId, negative }) {
  return (
    <div className="border border-zinc-300 p-2 bg-zinc-50">
      <div className="text-[9px] uppercase font-mono text-zinc-500 leading-tight mb-0.5">{label}</div>
      <div className={`font-display font-black text-xl ${negative ? "text-red-700" : ""}`} data-testid={testId}>
        {negative ? "- " : ""}RM {Number(value || 0).toFixed(2)}
      </div>
      <div className="text-[9px] font-mono text-zinc-400 mt-0.5 no-print">auto · locked</div>
    </div>
  );
}

function EditableRMStat({ label, value, auto, onChange, onReset, isOverridden, testId, negative }) {
  return (
    <div className={`border ${isOverridden ? "border-amber-400 bg-amber-50" : "border-zinc-300"} p-2 relative`}>
      <div className="text-[9px] uppercase font-mono text-zinc-500 leading-tight mb-0.5">{label}</div>
      <div className="flex items-baseline gap-1 justify-center">
        <span className={`text-[10px] font-mono ${negative ? "text-red-700" : "text-zinc-500"}`}>{negative ? "- RM" : "RM"}</span>
        <input
          type="number" min="0" step="0.01"
          value={Number(value || 0).toFixed(2)}
          onChange={(e) => onChange(e.target.value)}
          className={`payslip-input font-display font-black text-xl w-full text-right border border-dashed border-zinc-300 focus:border-[#002FA7] outline-none ${negative ? "text-red-700" : ""}`}
          data-testid={testId}
        />
      </div>
      <div className="flex items-center justify-between mt-0.5 no-print">
        <span className="text-[9px] font-mono text-zinc-400">auto RM{Number(auto || 0).toFixed(2)}</span>
        {isOverridden && onReset && (
          <button onClick={onReset} className="text-[9px] font-mono text-amber-700 hover:text-amber-900" title="Reset to auto" data-testid={`${testId}-reset`}>↺ auto</button>
        )}
      </div>
    </div>
  );
}

function Remark({ label, value }) {
  return (
    <>
      <div className="text-zinc-600">{label}</div>
      <div className="font-mono text-right">{value}</div>
    </>
  );
}

function LoanRow({ label, children }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className="py-1 text-sm">{label}</td>
      <td className="py-1 text-right">{children}</td>
    </tr>
  );
}

function prevMonth(month) {
  const [y, m] = month.split("-").map(Number);
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return `${py}-${String(pm).padStart(2, "0")}`;
}

function EditableText({ value, onChange, className = "", ...rest }) {
  return (
    <input
      value={value}
      onChange={onChange}
      className={`payslip-input border border-dashed border-transparent hover:border-zinc-300 focus:border-[#002FA7] outline-none px-1 ${className}`}
      {...rest}
    />
  );
}
